import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { validateEmail, validateUsername, validatePassword, sanitizeInput, sanitizeEmail } from '../utils/validation'
import './LoginModal.css'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onLoginSuccess: (user: { email: string; name: string; username: string; id: string }) => void
}

const LoginModal = ({ isOpen, onClose, onLoginSuccess }: LoginModalProps) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('')
      setPassword('')
      setName('')
      setUsername('')
      setError('')
      setLoading(false)
      setIsSignUp(false)
      setVerificationSent(false)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (isSignUp) {
        // === SIGN UP ===
        if (!email || !name || !username) {
          setError('Please enter email, name, and username')
          setLoading(false)
          return
        }

        if (!validateEmail(email)) {
          setError('Please enter a valid email address')
          setLoading(false)
          return
        }

        const usernameValidation = validateUsername(username)
        if (!usernameValidation.valid) {
          setError(usernameValidation.error || 'Invalid username')
          setLoading(false)
          return
        }

        if (!password) {
          setError('Password is required')
          setLoading(false)
          return
        }

        const passwordValidation = validatePassword(password)
        if (!passwordValidation.valid) {
          setError(passwordValidation.error || 'Invalid password')
          setLoading(false)
          return
        }

        const sanitizedEmail = sanitizeEmail(email)
        const sanitizedName = sanitizeInput(name)
        const sanitizedUsername = sanitizeInput(username).toLowerCase()

        // Check if username is already taken
        const { data: existingUsername } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', sanitizedUsername)
          .single()

        if (existingUsername) {
          setError('This username is already taken. Please choose another.')
          setLoading(false)
          return
        }

        // Create account with Supabase Auth
        const { error: signUpError } = await supabase.auth.signUp({
          email: sanitizedEmail,
          password,
          options: {
            data: {
              name: sanitizedName,
              username: sanitizedUsername,
            },
          },
        })

        if (signUpError) {
          if (signUpError.message.includes('already registered')) {
            setError('An account with this email already exists. Please sign in instead.')
          } else {
            setError(signUpError.message)
          }
          setLoading(false)
          return
        }

        setVerificationSent(true)
        setError('')
      } else {
        // === SIGN IN ===
        if (!email || !password) {
          setError('Please enter email and password')
          setLoading(false)
          return
        }

        const sanitizedEmail = sanitizeEmail(email)

        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email: sanitizedEmail,
          password,
        })

        if (signInError) {
          setError('Invalid email or password.')
          setLoading(false)
          return
        }

        if (data.user) {
          // Fetch the profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.user.id)
            .single()

          if (profile) {
            onLoginSuccess({
              id: profile.id,
              email: profile.email,
              name: profile.name,
              username: profile.username,
            })
          }
          setEmail('')
          setPassword('')
          setError('')
          onClose()
        }
      }
    } catch (err) {
      console.error(isSignUp ? 'Sign up error:' : 'Sign in error:', err)
      setError(isSignUp ? 'Sign up failed. Please try again.' : 'Sign in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError('')
    setLoading(true)
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: siteUrl,
      },
    })
    if (error) {
      setError('Google sign in failed. Please try again.')
      setLoading(false)
    }
    // On success, Supabase redirects — the auth listener in AuthContext picks up the session
  }

  const handleCreateAccount = () => {
    setIsSignUp(true)
    setError('')
    setVerificationSent(false)
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <span></span>
          <span></span>
        </button>
        <h2 className="modal-title">welcome to the signal</h2>
        <p className="modal-subtitle">
          {isSignUp
            ? verificationSent
              ? 'check your inbox to verify your email'
              : 'create an account to access exclusive content'
            : 'access exclusive content for community members'}
        </p>

        {verificationSent ? (
          <div className="verification-message">
            <p>We've sent a verification email to <strong>{email}</strong>. Please check your inbox and click the link to activate your account.</p>
            <button
              type="button"
              className="modal-button"
              onClick={() => {
                setIsSignUp(false)
                setVerificationSent(false)
                setEmail('')
                setName('')
              }}
            >
              back to sign in
            </button>
          </div>
        ) : (
          <>
            <form className="modal-form" onSubmit={handleSubmit}>
              {isSignUp && (
                <>
                  <div className="form-group">
                    <label className="form-label" htmlFor="signup-name">name</label>
                    <input
                      type="text"
                      id="signup-name"
                      value={name}
                      onChange={(e) => setName(sanitizeInput(e.target.value))}
                      className="form-input"
                      placeholder="your name"
                      required
                      disabled={loading}
                      autoComplete="name"
                      maxLength={50}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="signup-username">username</label>
                    <input
                      type="text"
                      id="signup-username"
                      value={username}
                      onChange={(e) => {
                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                        setUsername(val)
                      }}
                      className="form-input"
                      placeholder="username"
                      required
                      disabled={loading}
                      autoComplete="username"
                      minLength={3}
                      maxLength={20}
                      pattern="[a-z0-9_]+"
                    />
                    <small className="form-hint">3-20 characters, letters, numbers, and underscores only</small>
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="login-email">email</label>
                <input
                  type="email"
                  id="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={isSignUp ? 'signup-password' : 'login-password'}>
                  password
                </label>
                <input
                  type="password"
                  id={isSignUp ? 'signup-password' : 'login-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  minLength={8}
                />
                {isSignUp && (
                  <small className="form-hint">At least 8 characters with letters and numbers</small>
                )}
              </div>
              {error && <div className="form-error">{error}</div>}
              <button type="submit" className="modal-button" disabled={loading}>
                {loading
                  ? (isSignUp ? 'creating account...' : 'signing in...')
                  : (isSignUp ? 'create account' : 'sign in')}
              </button>
            </form>

            {!isSignUp && (
              <>
                <div className="modal-divider">
                  <span>or</span>
                </div>
                <button
                  type="button"
                  className="modal-button google-button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <i className="fab fa-google"></i>
                  continue with google
                </button>
              </>
            )}

            <div className="modal-footer">
              {isSignUp ? (
                <button
                  type="button"
                  className="modal-footer-link"
                  onClick={() => {
                    setIsSignUp(false)
                    setError('')
                  }}
                >
                  back to sign in
                </button>
              ) : (
                <button
                  type="button"
                  className="modal-footer-link"
                  onClick={handleCreateAccount}
                >
                  create account
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

export default LoginModal
